import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, CheckCircle2, Users, Database, Brain, TrendingUp, ArrowLeft, Sparkles } from 'lucide-react';

export default function IntelligentCrmBlog() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Back Button */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <Link to="/resources/blogs" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to Blogs
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div data-animate="hero" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold tracking-wider mb-4 rounded">
            BUSINESS GROWTH
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight text-zinc-900">
            Why Every Small Business Needs an Intelligent CRM (Not Just a Contact List)
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 mb-8 pb-8 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-zinc-400" />
              <span>28 December 2024</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              <span>6 min read</span>
            </div>
            <div>
              <span className="font-medium">By Digital Trent Team</span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section data-animate="intro" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="prose prose-lg max-w-none text-zinc-700">
            <p className="text-lg leading-relaxed mb-6">
              If you're still tracking customers in a spreadsheet, sticky notes, or scattered across different tools, you're not just being disorganised—<strong className="text-zinc-900">you're leaving serious money on the table</strong>.
            </p>
            <p className="leading-relaxed mb-6">
              Here's why: when a hot lead emails you Tuesday, then texts Friday, then calls the following Monday, can you see their full history in one place? Do you know what content they've engaged with? Which products they've viewed? Their actual pain points?
            </p>
            <p className="leading-relaxed">
              An intelligent CRM isn't just a fancy address book—it's the difference between guessing what customers want and actually knowing. Let me show you why it's the best investment you'll make this year.
            </p>
          </div>
        </section>

        {/* The Spreadsheet Problem */}
        <section data-animate="problem" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('problem') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Database size={22} className="text-white" />
            </div>
            Why Spreadsheets Are Costing You Sales
          </h2>

          <p className="text-zinc-700 mb-6 leading-relaxed text-lg">
            Using spreadsheets or basic contact lists creates these hidden problems:
          </p>

          <div className="space-y-4 mb-8">
            {[
              { problem: 'No Context, No Conversion', impact: 'You can\'t see customer history, so every interaction starts from scratch' },
              { problem: 'Missed Follow-Ups', impact: 'No automated reminders mean hot leads go cold whilst you\'re busy' },
              { problem: 'Scattered Information', impact: 'Emails in Gmail, notes in Slack, calls logged nowhere—critical details get lost' },
              { problem: 'Zero Automation', impact: 'You manually track every lead stage, send every follow-up, remember every task' },
              { problem: 'No Analytics', impact: 'You can\'t see which marketing channels actually drive sales or customer value' }
            ].map((item, i) => (
              <div key={i} className="bg-red-50 border border-red-200 p-6 rounded-lg">
                <h4 className="font-bold text-zinc-900 mb-2">{item.problem}</h4>
                <p className="text-zinc-600 text-sm">{item.impact}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-8 rounded-r-lg">
            <p className="text-zinc-900 font-bold text-lg mb-2">The Hidden Cost</p>
            <p className="text-zinc-700 leading-relaxed">Research shows businesses without proper CRM lose 20-30% of potential revenue due to poor follow-up, lack of personalisation, and missed opportunities. For a £100k/year business, that's £20-30k left on the table.</p>
          </div>
        </section>

        {/* What Intelligent CRM Actually Does */}
        <section data-animate="solution" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('solution') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain size={22} className="text-white" />
            </div>
            What an Intelligent CRM Actually Does for You
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            Modern intelligent CRMs powered by AI don't just store data—they work for you 24/7. Here's how:
          </p>

          <div className="space-y-6">
            {[
              {
                num: '1',
                title: 'Automatic Customer Intelligence',
                desc: 'AI captures every interaction—emails, calls, website visits, social engagement—and builds a complete picture of each customer automatically.',
                benefit: 'Never miss context again',
                color: 'blue'
              },
              {
                num: '2',
                title: 'Smart Lead Scoring & Prioritisation',
                desc: 'AI analyses behaviour to tell you which leads are hot right now and what they\'re interested in, so you know exactly who to call first.',
                benefit: '3-5x higher close rates',
                color: 'green'
              },
              {
                num: '3',
                title: 'Automated Follow-Up & Nurturing',
                desc: 'Set up trigger-based workflows once, and the CRM automatically sends personalised follow-ups, reminders, and offers based on customer behaviour.',
                benefit: 'Zero leads slip through cracks',
                color: 'purple'
              },
              {
                num: '4',
                title: 'Predictive Analytics',
                desc: 'AI predicts which customers are likely to buy, likely to churn, and identifies upsell opportunities you\'d never spot manually.',
                benefit: 'Proactive, not reactive',
                color: 'orange'
              }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300">
                {/* Faded Micro Grid */}
                <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none opacity-[0.06] overflow-hidden rounded-tl-xl">
                  <div
                    style={{
                      backgroundImage: `linear-gradient(${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : '#f97316'} 0.5px, transparent 0.5px),
                                        linear-gradient(90deg, ${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : '#f97316'} 0.5px, transparent 0.5px)`,
                      backgroundSize: '8px 8px',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shadow-sm">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{item.title}</h3>
                    <p className="text-zinc-700 mb-3 leading-relaxed">{item.desc}</p>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <Sparkles size={16} className="text-green-600" />
                      <span className="text-sm font-semibold text-green-700">{item.benefit}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* The ROI Reality */}
        <section data-animate="roi" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('roi') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp size={22} className="text-white" />
            </div>
            How an Intelligent CRM Pays for Itself
          </h2>

          <p className="text-zinc-700 mb-8 leading-relaxed text-lg">
            Let's talk numbers. Here's the actual ROI from our customers:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {[
              { metric: 'More Closed Deals', value: '+37%', detail: 'Better follow-up and prioritisation' },
              { metric: 'Faster Sales Cycle', value: '-40%', detail: 'Instant access to customer context' },
              { metric: 'Higher Customer Value', value: '+25%', detail: 'Identifying upsell opportunities' },
              { metric: 'Time Saved Weekly', value: '8-12 hrs', detail: 'Automated tasks and workflows' }
            ].map((item, i) => (
              <div key={i} className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
                <p className="text-sm text-zinc-600 mb-1 font-medium">{item.metric}</p>
                <p className="text-4xl font-bold text-blue-600 mb-2">{item.value}</p>
                <p className="text-sm text-zinc-600">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="bg-green-50 border-2 border-green-200 p-6 rounded-xl">
            <h4 className="font-bold text-lg mb-3 text-zinc-900">Real Example:</h4>
            <p className="text-zinc-700 leading-relaxed mb-4">
              A marketing agency with £15k/month revenue implemented an intelligent CRM. Within 90 days:
            </p>
            <ul className="space-y-2 text-zinc-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Revenue increased to £22k/month (47% growth)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Lead-to-customer conversion improved from 3% to 11%</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Owner reclaimed 10 hours/week from manual admin</span>
              </li>
            </ul>
            <p className="text-zinc-900 font-bold mt-4">ROI: 890% in first year</p>
          </div>
        </section>

        {/* What to Look For */}
        <section data-animate="features" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-zinc-900">What to Look for in an Intelligent CRM</h2>

          <div className="text-zinc-700 leading-relaxed space-y-4 mb-8">
            <p className="text-lg">
              Not all CRMs are created equal. Here's what makes one "intelligent":
            </p>

            <div className="bg-white border border-zinc-200 p-8 rounded-xl">
              <ul className="space-y-4">
                {[
                  'AI-powered lead scoring and qualification',
                  'Automatic activity capture (emails, calls, meetings)',
                  'Behavioural triggers for automated workflows',
                  'Integrated marketing automation (not a separate tool)',
                  'Predictive analytics and insights',
                  'Mobile access for on-the-go updates',
                  'Simple setup—not requiring a consultant'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-lg text-zinc-700 leading-relaxed">
            The right intelligent CRM should feel like having a personal assistant who remembers everything, follows up automatically, and tells you exactly what to focus on to maximise revenue.
          </p>
        </section>

        {/* Conclusion */}
        <section data-animate="conclusion" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('conclusion') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-zinc-900">The Bottom Line</h2>

          <div className="text-zinc-700 leading-relaxed space-y-6">
            <p className="text-lg">
              An intelligent CRM isn't an expense—it's an investment that pays for itself many times over. <strong className="text-zinc-900">You're either tracking customers intelligently or losing money to competitors who are.</strong>
            </p>

            <p className="text-lg">
              The businesses crushing it in 2025 aren't the ones with the biggest teams or budgets. They're the ones using intelligent systems to deliver personalised experiences at scale, follow up instantly, and make data-driven decisions.
            </p>

            <p className="text-lg">
              Stop treating your customer relationships like a part-time hobby. Invest in an intelligent CRM and watch what happens when you actually know your customers.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section data-animate="cta" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 lg:p-10 rounded-2xl text-center shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready for an Intelligent CRM That Works for You?</h3>
            <p className="text-lg text-blue-50 mb-10 max-w-2xl mx-auto leading-relaxed">
              Digital Trent combines intelligent CRM with AI-powered marketing automation—everything you need in one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="group bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                Start Free Trial
                <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/company/contact')}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300"
              >
                See How It Works
              </button>
            </div>
            <p className="text-sm text-blue-50 mt-6">No credit card required • 14-day free trial • Cancel any time</p>
          </div>
        </section>
      </article>
    </div>
  );
}
